import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FEEDS, VISIBLE_STRATEGIES } from '../constants';

export default function TabBar({ activeFeed, onChange }) {
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState({ left: 8, top: 86, width: 300 });
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuRect = useCallback(() => {
    if (!wrapperRef.current || typeof window === 'undefined') return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const margin = 8;
    const width = Math.min(340, window.innerWidth - margin * 2);
    const left = Math.min(
      Math.max(margin, rect.left),
      window.innerWidth - width - margin
    );

    setMenuRect({
      left,
      top: rect.bottom + 2,
      width,
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    updateMenuRect();

    const onDoc = (e) => {
      const inTrigger = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onPlace = () => updateMenuRect();

    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onPlace);
    window.addEventListener('scroll', onPlace, true);

    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onPlace);
      window.removeEventListener('scroll', onPlace, true);
    };
  }, [open, updateMenuRect]);

  const strategies = VISIBLE_STRATEGIES.map(id => FEEDS[id]).filter(Boolean);
  const portfolio = FEEDS.UNIFIED;
  const activeStrategy = strategies.find(f => f.id === activeFeed) || null;
  const portfolioActive = activeFeed === 'UNIFIED';

  const toggleStrategies = () => {
    if (open) {
      setOpen(false);
      return;
    }
    updateMenuRect();
    setOpen(true);
  };

  const menu = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={menuRef}
      role="listbox"
      style={{
        position: 'fixed',
        top: menuRect.top,
        left: menuRect.left,
        width: menuRect.width,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'min(64vh, 390px)',
        overflowX: 'hidden',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: 'var(--tv-surface)',
        border: '1px solid var(--tv-border)',
        borderRadius: 4,
        boxShadow: '0 12px 32px rgba(20,30,55,0.22)',
        zIndex: 99999,
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {strategies.map(f => {
        const isActive = f.id === activeFeed;
        return (
          <button
            key={f.id}
            type="button"
            role="option"
            aria-selected={isActive}
            onClick={() => { onChange(f.id); setOpen(false); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              minHeight: 38,
              padding: '8px 10px',
              background: isActive ? `${f.color}12` : 'transparent',
              border: 'none',
              borderLeft: `3px solid ${isActive ? f.color : 'transparent'}`,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.03em',
              color: isActive ? f.color : 'var(--tv-text)',
              textAlign: 'left',
              transition: 'background 0.12s',
              borderRadius: 3,
            }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--tv-surface-hi)'; }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {f.label}
            </span>
            <span style={{
              marginLeft: 'auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 10,
              color: 'var(--tv-text-muted)',
              fontWeight: 400,
            }}>
              {f.sublabel}
            </span>
          </button>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <div className="tabbar">
      <div ref={wrapperRef} style={{ position: 'relative', display: 'flex' }}>
        <button
          type="button"
          onClick={toggleStrategies}
          className={`tab-btn${activeStrategy ? ' active' : ''}`}
          style={{
            color: activeStrategy ? activeStrategy.color : undefined,
            borderBottomColor: activeStrategy ? activeStrategy.color : undefined,
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className="tab-dot"
            style={{ background: activeStrategy ? activeStrategy.color : 'rgba(120,123,134,0.3)' }}
          />
          <span>{activeStrategy ? activeStrategy.label : 'Strategies'}</span>
          {activeStrategy && <span className="tab-sublabel">{activeStrategy.sublabel}</span>}
          <span style={{
            fontSize: 9,
            marginLeft: 4,
            color: 'currentColor',
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}>
            v
          </span>
        </button>
      </div>

      {menu}

      <button
        type="button"
        onClick={() => onChange(portfolio.id)}
        className={`tab-btn${portfolioActive ? ' active' : ''}`}
        style={{
          color: portfolioActive ? portfolio.color : undefined,
          borderBottomColor: portfolioActive ? portfolio.color : undefined,
        }}
      >
        <span
          className="tab-dot"
          style={{ background: portfolioActive ? portfolio.color : 'rgba(120,123,134,0.3)' }}
        />
        <span>{portfolio.label}</span>
        {portfolio.sublabel && <span className="tab-sublabel">{portfolio.sublabel}</span>}
      </button>
    </div>
  );
}
